// Runtime, user-editable configuration for the AI terminal (packages/react-terminal).
//
// The build-time `LLM_TERMINAL_CONFIG` (webpack DefinePlugin) only SEEDS the defaults; the
// values here are what the user edits in the settings dialog and are persisted to this
// browser's localStorage. Because the terminal calls the LLM provider directly from the
// browser with no backend of ours in the path, the API keys are the user's own and live only
// in their localStorage — the same trade-off the underlying package documents for
// `persistApiKeys`. Nothing is transmitted to a Gridd/DeckAI/SlideCraft server.
import { LLM_TERMINAL_CONFIG } from "@/lib/config";
import { isElectron } from "@/lib/runtimeEnv";

const STORAGE_KEY = "slidecraft:ai-terminal-settings";

export type TerminalProviderId = "anthropic" | "openai" | "ollama" | "qwen";

export interface TerminalSettings {
  /** Which provider the terminal talks to by default. */
  activeProviderId: TerminalProviderId;
  /** Instruction prepended to every conversation. */
  systemPrompt: string;
  anthropic: { apiKey: string; model: string };
  openai: { apiKey: string; model: string };
  ollama: { baseUrl: string; model: string };
  /** Docker Model Runner (Qwen &c.): a keyless, OpenAI-compatible local endpoint. */
  qwen: { baseUrl: string; model: string };
}

export const DEFAULT_SYSTEM_PROMPT =
  "You are the AI assistant embedded in SlideCraft Studio, a slide-deck editor. " +
  "Help the user build their deck. Use the provided tools to modify slides when asked; " +
  "each slide is identified by its id.";

/** Settings seeded purely from the build-time defaults — the pre-save baseline. */
export function defaultTerminalSettings(): TerminalSettings {
  const c = LLM_TERMINAL_CONFIG;
  return {
    // With no cloud key the local runner is the only thing that can answer, and Docker Model
    // Runner is the one this app wires end-to-end: the dev server proxies it, and the desktop
    // shell routes it through the main process (§14.5). Ollama stays in the list for anyone
    // running that instead — it is a pick, not a default.
    activeProviderId: c.anthropicApiKey
      ? "anthropic"
      : c.openaiApiKey
        ? "openai"
        : "qwen",
    systemPrompt: DEFAULT_SYSTEM_PROMPT,
    anthropic: { apiKey: c.anthropicApiKey, model: c.anthropicModel },
    openai: { apiKey: c.openaiApiKey, model: c.openaiModel },
    ollama: { baseUrl: c.ollamaBaseUrl, model: c.ollamaModel },
    // §14.5.4 The build-time default is the dev server's same-origin proxy path, which does
    // not exist in the desktop shell — `normalizeQwenBaseUrl` maps it to the direct endpoint
    // there. Seeded through the same function as a persisted value so the two cannot drift.
    qwen: { baseUrl: normalizeQwenBaseUrl(c.qwenBaseUrl), model: c.qwenModel },
  };
}

/** Load saved settings, layering them over the build-time defaults (missing keys fall back). */
export function loadTerminalSettings(): TerminalSettings {
  const base = defaultTerminalSettings();
  if (typeof window === "undefined") return base;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw) as Partial<TerminalSettings>;
    const qwen = { ...base.qwen, ...saved.qwen };
    return {
      activeProviderId: saved.activeProviderId ?? base.activeProviderId,
      systemPrompt: saved.systemPrompt ?? base.systemPrompt,
      anthropic: { ...base.anthropic, ...saved.anthropic },
      openai: { ...base.openai, ...saved.openai },
      ollama: { ...base.ollama, ...saved.ollama },
      qwen: {
        ...qwen,
        baseUrl: normalizeQwenBaseUrl(qwen.baseUrl),
        model: normalizeQwenModel(qwen.model),
      },
    };
  } catch {
    // Corrupt JSON or unavailable storage — fall back to defaults rather than crash.
    return base;
  }
}

// Which Docker Model Runner URL is reachable depends on the runtime, and getting it wrong is
// silent: the terminal simply never answers.
//
//   • In the browser, DMR must be reached through the same-origin `/__dmr` reverse proxy (dev
//     server / nginx). A direct `http://localhost:12434` URL is blocked by CORS, and by
//     mixed-content once the app is served over HTTPS. Earlier builds seeded that direct URL,
//     so any persisted copy is rewritten to the proxied path.
//   • §14.5.4 In the desktop shell the opposite holds. There is no reverse proxy — the app is
//     served off a loopback static server — and the direct URL is the one the fetch shim
//     recognises and routes to the main process over IPC (`src/lib/desktopLlm.ts`), where
//     there is no CORS at all. So a same-origin path is rewritten back to the direct endpoint.
//
// Either way, a URL the user deliberately pointed elsewhere (their own gateway, another port)
// is left untouched: the model and the port stay theirs to edit.
const DMR_PROXY_BASE_URL = "/__dmr/engines/v1";
const DMR_DIRECT_BASE_URL = "http://localhost:12434/engines/v1";

function normalizeQwenBaseUrl(url: string): string {
  const trimmed = url.trim();
  if (isElectron()) {
    return trimmed.startsWith("/") ? DMR_DIRECT_BASE_URL : trimmed;
  }
  return /^https?:\/\/(localhost|127\.0\.0\.1):12434(\/|$)/i.test(trimmed)
    ? DMR_PROXY_BASE_URL
    : trimmed;
}

// Model ids this app used to seed, which are almost certainly not pulled on the machine any
// more. A stale id fails at request time with a bare 404 from the runner — which reads as
// "the terminal is broken" rather than "that model is gone" — so a persisted copy of a
// previous *default* is rewritten to the current one. Anything the user typed themselves is
// left exactly as they typed it; the list only ever holds ids this app chose for them.
const SUPERSEDED_QWEN_MODELS = new Set(["ai/qwen2.5"]);

function normalizeQwenModel(model: string): string {
  const trimmed = model.trim();
  return SUPERSEDED_QWEN_MODELS.has(trimmed)
    ? LLM_TERMINAL_CONFIG.qwenModel
    : trimmed;
}

export function saveTerminalSettings(settings: TerminalSettings): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Quota exceeded or storage disabled (private mode) — the in-memory settings still apply
    // for this session; they just won't survive a reload.
  }
}
